import { designTokens } from "@itandi/itandi-bb-ui";
import { Box } from "@itandi/itandi-bb-ui/Box";
import { PresentationChip } from "@itandi/itandi-bb-ui/PresentationChip";
import { SecondaryTab } from "@itandi/itandi-bb-ui/SecondaryTab";
import { SecondaryTabs } from "@itandi/itandi-bb-ui/SecondaryTabs";
import { useCallback, useState } from "react";
import { DealFileListClient } from "~/components/common/deals/show/files/DealFileListClient";
import { DealFileListVendor } from "~/components/common/deals/show/files/DealFileListVendor";
import { DealMainSpLinksClient } from "~/components/common/deals/show/main/DealMainSpLinksClient";
import { DealMainSpLinksVendor } from "~/components/common/deals/show/main/DealMainSpLinksVendor";
import { DealMainWorkflowClient } from "~/components/common/deals/show/main/DealMainWorkflowClient";
import { DealMainWorkflowVendor } from "~/components/common/deals/show/main/DealMainWorkflowVendor";
import { DealHistoryClientList } from "~/components/common/deals/show/main/histories/DealHistoryClientList";
import { DealHistoryVendorList } from "~/components/common/deals/show/main/histories/DealHistoryVendorList";
import { CompletionReportImageClientPanel } from "~/components/specifics/deals/parts/CompletionReportImageClientPanel";
import { CompletionReportImageVendorPanel } from "~/components/specifics/deals/parts/CompletionReportImageVendorPanel";
import { EstimationClientPanel } from "~/components/specifics/deals/parts/EstimationClientPanel";
import { EstimationVendorPanel } from "~/components/specifics/deals/parts/EstimationVendorPanel";
import { InspectionReportImageClientPanel } from "~/components/specifics/deals/parts/InspectionReportImageClientPanel";
import { InspectionReportImageVendorPanel } from "~/components/specifics/deals/parts/InspectionReportImageVendorPanel";
import type { Deal } from "~/models/Deal";
import { estimationHeaderStatus, matchDealType } from "~/models/Deal";
import { IN_CONSTRUCTION } from "~/models/Status";
import Styles from "./DealMain.module.scss";

type Props = {
	deal: Deal;
	isAdmin: boolean;
	isVendor: boolean;
};

export type TabType =
	| "workflow"
	| "reports"
	| "estimation"
	| "histories"
	| "files";

const TAB_OPTIONS = [
	{ label: "手続き", value: "workflow" },
	{ label: "報告画像", value: "reports" },
	{ label: "見積り", value: "estimation" },
	{ label: "履歴", value: "histories" },
	{ label: "ファイル", value: "files" }
] as const satisfies readonly { label: string; value: TabType }[];

export function DealMain({ deal, isVendor, isAdmin }: Props) {
	const [tabType, setTabType] = useState<TabType>("workflow");
	const showCompleteImagePanel =
		deal.statusId >= IN_CONSTRUCTION ||
		matchDealType(deal, "repair") ||
		matchDealType(deal, "other");
	const handleChangeTab = useCallback((newTabType: TabType): void => {
		setTabType(newTabType);
	}, []);
	const estimationStatus = estimationHeaderStatus(deal);

	return (
		<div className={Styles.DealMain}>
			{isVendor ? (
				<DealMainSpLinksVendor isAdmin={isAdmin} />
			) : (
				<DealMainSpLinksClient isAdmin={isAdmin} />
			)}
			<Box mb={designTokens["spacing/m"]}>
				<SecondaryTabs value={tabType}>
					{TAB_OPTIONS.map(({ label, value }) => (
						<SecondaryTab
							key={value}
							onClick={() => {
								handleChangeTab(value);
							}}
							rightArea={
								value === "estimation" &&
								estimationStatus.label !== "" &&
								estimationStatus.variant !== null ? (
									<PresentationChip variant={estimationStatus.variant}>
										{estimationStatus.label}
									</PresentationChip>
								) : null
							}
							value={value}
						>
							{label}
						</SecondaryTab>
					))}
				</SecondaryTabs>
			</Box>
			<div className={tabType === "workflow" ? Styles.Show : Styles.Hide}>
				{isVendor ? <DealMainWorkflowVendor /> : <DealMainWorkflowClient />}
			</div>
			<div className={tabType === "reports" ? Styles.Show : Styles.Hide}>
				{isVendor ? (
					<div className={Styles.Card}>
						<InspectionReportImageVendorPanel />
						{showCompleteImagePanel ? (
							<CompletionReportImageVendorPanel />
						) : null}
					</div>
				) : (
					<div className={Styles.Card}>
						<InspectionReportImageClientPanel />
						{showCompleteImagePanel ? (
							<CompletionReportImageClientPanel />
						) : null}
					</div>
				)}
			</div>
			{tabType === "histories" && (
				// NOTE: mutateを実行するためにタブを開く度にrenderingし直す
				<div className={Styles.Show}>
					<div className={Styles.Card}>
						{isVendor ? (
							<DealHistoryVendorList dealId={deal.id} />
						) : (
							<DealHistoryClientList dealId={deal.id} />
						)}
					</div>
				</div>
			)}
			<div className={tabType === "estimation" ? Styles.Show : Styles.Hide}>
				<div className={Styles.Card}>
					{isVendor ? (
						<EstimationVendorPanel deal={deal} />
					) : (
						<EstimationClientPanel deal={deal} />
					)}
				</div>
			</div>
			<div className={tabType === "files" ? Styles.Show : Styles.Hide}>
				{isVendor ? <DealFileListVendor /> : <DealFileListClient />}
			</div>
		</div>
	);
}
